import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from './api';

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('tm_jwt');
    const hasCookie = document.cookie.includes('session_token');

    if (!token && !hasCookie) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const { data } = await api.get('/auth/me');
      setUser(data.user);
    } catch {
      localStorage.removeItem('tm_jwt');
      document.cookie = 'session_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Skip auth check when returning from OAuth callback (AuthCallback handles it)
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (!user) return;

    let timeoutId;
    const INACTIVITY_LIMIT = 15 * 60 * 1000; // 15 minutes in milliseconds

    const resetTimer = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        console.log('[HIPAA] Automatically logging out due to 15 minutes of inactivity');
        logout();
      }, INACTIVITY_LIMIT);
    };

    const handleStorageChange = (e) => {
      if (e.key === 'tm_jwt' && !e.newValue) {
        setUser(null);
        if (!window.location.pathname.startsWith('/login') && !window.location.pathname.startsWith('/signup')) {
          window.location.href = '/login';
        }
      }
    };

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(name => document.addEventListener(name, resetTimer));
    window.addEventListener('storage', handleStorageChange);

    resetTimer();

    return () => {
      clearTimeout(timeoutId);
      events.forEach(name => document.removeEventListener(name, resetTimer));
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [user]);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    localStorage.setItem('tm_jwt', data.token);
    // Re-fetch via /me to include permissions[]
    await checkAuth();
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post('/auth/register', payload);
    localStorage.setItem('tm_jwt', data.token);
    await checkAuth();
    return data.user;
  };

  const acceptInvite = async (payload) => {
    const { data } = await api.post('/invitations/accept', payload);
    localStorage.setItem('tm_jwt', data.token);
    await checkAuth();
    return data.user;
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch { /* noop */ }
    localStorage.removeItem('tm_jwt');
    document.cookie = 'session_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, acceptInvite, refresh: checkAuth, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
